import {
  CALCULATE_PRICING_FAILURE,
  CREATE_REG_CART_FAILURE,
  FINALIZE_CHECKOUT_FAILURE,
  START_MODIFICATION_FAILURE,
  UPDATE_REG_CART_FAILURE,
  ADD_GROUP_MEMBER_FAILURE,
  INITIATE_WAITLIST_REGISTRATION_FAILURE,
  START_WAITLIST_REGISTRATION_CHECKOUT_FAILURE
} from './regCart/actionTypes';
import { getDefaultRegistrationPath } from 'event-widgets/redux/selectors/appData';
import { get, every } from 'lodash';
import getJSONValue from 'nucleus-widgets/utils/fields/getJSONValue';
import { accessRulesJsonPath } from 'event-widgets/utils/registrationPath';
import { PRIVATE_ALL_TARGETED_LISTS, PRIVATE_LIMITED_TARGETED_LISTS } from 'event-widgets/clients/RegistrationPathType';

const PAYMENT_STATUS_FAILED = 'PaymentFailed';

export const getValidations = (response, validationCode, parameters) => {
  return get(response, 'validationMessages', [])
    .filter(validation => {
      return validation.localizationKey === validationCode
        && (!parameters || every(parameters, (parameter, key) => validation.parametersMap[key] === parameter));
    });
};

/**
 * Determines if the response has the given validation with the specified parameters.
 */
const responseHasValidation = (response, validationCode, parameters) => {
  return getValidations(response, validationCode, parameters).length > 0;
};

/**
 * Determines if the errors has the given validation with the specified parameters.
 */
const hasValidation = (error, validationCode, parameters) => {
  return responseHasValidation(get(error, 'responseBody', undefined), validationCode, parameters);
};

const isPrivateEvent = (error) => {
  return hasValidation(error, 'REGAPI.EMAIL_ONLY_INVITEE')
    || hasValidation(error, 'REGAPI.ATTENDEE_NOT_IN_ANY_TARGET_LIST')
    || hasValidation(error, 'REGAPI.ATTENDEE_NOT_IN_SPECIFIC_TARGET_LIST')
    || hasValidation(error, 'REGAPI.EVENT_TO_EVENT_PREREQUISITE_FAILED');
};

const isAttendeeNotAllowedByCustomLogic = (error) => {
  return hasValidation(error, 'REGAPI.ATTENDEE_ASSIGNED_NOT_ALLOWED_REG_TYPE');
};

const isTransactionInProcessingError = (error) => {
  return hasValidation(error, 'REGAPI.REGMOD_INVITEE_TRANSACTION_IN_PROCESSING');
};

export const getKnownErrorOrNull = (validation) => {
  switch (validation) {
    case 'REGAPI.REGAPI_VOUCHER_CODE_REQUIRED':
      return 'EventGuestside_ApiError_VoucherCodeRequired__resx';
    case 'REGAPI.IDENTITY_SOURCE_ID_MODIFIED':
      return 'EventGuestside_ApiError_SourceIdModified__resx';
    case 'REGAPI.IDENTITY_EMAIL_MODIFIED':
      return 'EventGuestside_ApiError_EmailModified__resx';
    case 'REGAPI.ID_CONFIRMATION_CONTACT_IDENTIFICATION_EXCEPTION':
      return 'EventGuestside_ApiError_ContactNotFound__resx';
    case 'REGAPI.ID_CONFIRMATION_SOURCE_ID_NO_MATCH':
      return 'EventGuestside_ApiError_SourceIdNoMatch__resx';
    case 'REGAPI.ID_CONFIRMATION_SOURCE_ID_MULTIPLE_MATCHED':
      return 'EventGuestside_ApiError_SourceIdMultipleMatches__resx';
    case 'REGAPI.ID_CONFIRMATION_SOURCE_ID_INCONSISTENT_MATCH':
      return 'EventGuestside_ApiError_SourceIdInconsistentMatch__resx';
    case 'REGAPI.DUP_MATCH_KEY_VIOLATION':
      return 'EventGuestside_ApiError_DuplicateMatchKey__resx';
    case 'REGAPI.CHANGED_ID_CONFIRMATION_FIELDS':
      return 'EventGuestside_ApiError_ChangedIdConfirmation__resx';
    case 'REGAPI.FIELD_VALIDATION_ERROR_EMAIL_FORMAT':
    case 'QUESTION.INVALID_EMAIL':
    case 'REGAPI.ADMIN_EMAIL_FORMAT_INVALID':
    case 'REGAPI.ADMIN_EMAIL_DOMAIN_NOT_ALLOWED':
      return 'EventWidgets_Validations_EmailAddressFormat__resx';
    case 'REGAPI.DISCOUNT_INVALID':
      return 'EventWidgets_Validations_DiscountCodeInvalid__resx';
    case 'REGAPI.DISCOUNT_NONSTACKABLE':
      return 'EventWidgets_Validations_DiscountCodeNotStackable__resx';
    case 'REGAPI.DISCOUNT_NOT_APPLICABLE':
      return 'EventWidgets_Validations_DiscountCodeNotApplicable__resx';
    case 'REGAPI.DISCOUNT_ALREADY_APPLIED':
      return 'EventWidgets_Validations_DiscountCodeAlreadyApplied__resx';
    case 'REGAPI.ID_CONFIRMATION_SOURCE_ID_EMAIL_MISMATCH':
      return 'EventGuestside_ApiError_SourceIdEmailIdMismatch__resx';
    case 'REGAPI.REGMOD_PAST_MODIFICATION_DATE':
      return 'EventGuestSide_ApiError_RegModPastModificationDate__resx';
    case 'REGAPI.REGAPI.REGMOD_INVITEE_STATUS_INVALID':
      return 'EventGuestside_ApiError_RegModInviteeStatusInvalid__resx';
    default:
      return null;
  }
};

export const findKnownErrorResourceKey = (validations) => {
  return validations.map(validation => getKnownErrorOrNull(validation.localizationKey)).find(x => x);
};

export const getDeclineErrors = {
  isInviteeDeclined(error) {
    return hasValidation(error, 'REGAPI.INVALID_INVITEES_STATUS_DECLINED', { inviteeStatus: 'Declined' });
  },
  isInviteeRegistered(error) {
    return hasValidation(error, 'REGAPI.INVALID_INVITEES_STATUS_DECLINED', { inviteeStatus: 'Accepted' }) ||
      hasValidation(error, 'REGAPI.INVALID_EXISTING_INVITEES_STATUS_REGISTRATION',
        { inviteeStatus: 'PendingApproval' }) ||
      hasValidation(error, 'REGAPI.INVALID_EXISTING_INVITEES_STATUS_REGISTRATION',
        { inviteeStatus: 'DeniedApproval' });
  },
  isInviteeStatusInvalid(error) {
    return hasValidation(error, 'REGAPI.INVALID_INVITEES_STATUS_DECLINED');
  }
};
export const getRegisterErrors = {
  isInviteeRegistered(error) {
    return hasValidation(error, 'REGAPI.INVALID_EXISTING_INVITEES_STATUS_REGISTRATION',
      { inviteeStatus: 'Accepted' }) ||
      hasValidation(error, 'REGAPI.INVALID_EXISTING_INVITEES_STATUS_REGISTRATION',
        { inviteeStatus: 'PendingApproval' }) ||
      hasValidation(error, 'REGAPI.INVALID_EXISTING_INVITEES_STATUS_REGISTRATION',
        { inviteeStatus: 'DeniedApproval' });
  },
  isRegistrationClosed(error) {
    return hasValidation(error, 'REGAPI.CAPACITY_UNAVAILABLE') ||
      hasValidation(error, 'REGAPI.PRODUCT_NOT_OPEN_FOR_REGISTRATION') ||
      hasValidation(error, 'REGAPI.REGAPI_ADMISSION_CAPACITY_NOT_AVAILABLE') ||
      hasValidation(error, 'REGAPI.REGAPI_ADMISSION_CAPACITY_NOT_AVAILABLE_FOR_REGTYPE');
  },
  isEventClosed(error) {
    return hasValidation(error, 'REGAPI.EVENT_NOT_OPEN_FOR_REGISTRATION') ||
      hasValidation(error, 'REGAPI.EVENT_NOT_OPEN_FOR_REGISTRATION_INVITEE_STATUS');
  },
  isEventTemporaryClosed(error) {
    return hasValidation(error, 'REGAPI.EVENT_TEMPORARY_NOT_OPEN_FOR_REGISTRATION') ||
      hasValidation(error, 'REGAPI.EVENT_STATUS_WAITLIST_NOT_ALLOWED');
  },
  isCapacityFull(error) {
    return hasValidation(error, 'REGAPI.CAPACITY_UNAVAILABLE');
  },
  isInviteeAlreadyWaitlistedForClosedEvent: (error) => {
    return hasValidation(error, 'REGAPI.EVENT_NOT_OPEN_FOR_REGISTRATION_INVITEE_STATUS',
      { inviteeStatus: 'WaitListed' }) ||
      hasValidation(error, 'REGAPI.INVALID_EXISTING_INVITEES_STATUS_REGISTRATION',
        { inviteeStatus: 'WaitListed' }) ||
      hasValidation(error, 'REGAPI.INVALID_EXISTING_INVITEES_STATUS_WAITLIST',
        { inviteeStatus: 'WaitListed' });
  },
  isPrivateEvent,
  isAttendeeNotAllowedByCustomLogic
};

export const productErrors = {
  getInvalidSessionIds(error) {
    return getValidations(error.responseBody, 'REGAPI.PRODUCT_NOT_AVAILABLE')
      .filter(validation => validation.parametersMap && validation.parametersMap.productType === 'Session')
      .map(validation => validation.parametersMap.productId);
  },
  isAdmissionItemInvalid(error) {
    const validations = getValidations(error.responseBody, 'REGAPI.PRODUCT_NOT_AVAILABLE')
      .filter(validation => validation.parametersMap && validation.parametersMap.productType === 'AdmissionItem');
    return validations.length > 0;
  },
  isGroupRegistrationNotAllowed(error) {
    const validations = getValidations(error.responseBody, 'REGAPI.GROUP_REGISTRATION_NOT_ALLOWED');
    return validations && validations.length > 0;
  }
};

export const getUpdateErrors = {
  isPrivateEvent,
  isAttendeeNotAllowedByCustomLogic,
  isInviteeNotFound(error) {
    return error.responseStatus === 404;
  },
  isKnownError(error) {
    return hasValidation(error, 'REGAPI.IDENTITY_SOURCE_ID_MODIFIED')
      || hasValidation(error, 'REGAPI.IDENTITY_EMAIL_MODIFIED')
      || hasValidation(error, 'REGAPI.ID_CONFIRMATION_CONTACT_IDENTIFICATION_EXCEPTION')
      || hasValidation(error, 'REGAPI.ID_CONFIRMATION_SOURCE_ID_NO_MATCH')
      || hasValidation(error, 'REGAPI.ID_CONFIRMATION_SOURCE_ID_MULTIPLE_MATCHED')
      || hasValidation(error, 'REGAPI.ID_CONFIRMATION_SOURCE_ID_INCONSISTENT_MATCH')
      || hasValidation(error, 'REGAPI.DUP_MATCH_KEY_VIOLATION')
      || hasValidation(error, 'REGAPI.CHANGED_ID_CONFIRMATION_FIELDS')
      || hasValidation(error, 'REGAPI.FIELD_VALIDATION_ERROR_EMAIL_FORMAT')
      || hasValidation(error, 'QUESTION.INVALID_EMAIL')
      || hasValidation(error, 'REGAPI.DISCOUNT_INVALID')
      || hasValidation(error, 'REGAPI.DISCOUNT_NONSTACKABLE')
      || hasValidation(error, 'REGAPI.DISCOUNT_NOT_APPLICABLE')
      || hasValidation(error, 'REGAPI.DISCOUNT_ALREADY_APPLIED')
      || hasValidation(error, 'REGAPI.ADMIN_EMAIL_FORMAT_INVALID')
      || hasValidation(error, 'REGAPI.ADMIN_EMAIL_DOMAIN_NOT_ALLOWED')
      || hasValidation(error, 'REGAPI.ID_CONFIRMATION_SOURCE_ID_EMAIL_MISMATCH');
  },
  isInviteeAlreadyRegistered(error) {
    return hasValidation(error, 'REGAPI.INVALID_EXISTING_INVITEES_STATUS_REGISTRATION');
  },
  isDuplicateInvitee(error) {
    return hasValidation(error, 'REGAPI.DUPLICATE_INVITEE');
  },
  isPaymentProcessingError(error) {
    const response = get(error, 'responseBody', undefined);
    const statusCode = get(error, 'responseBody.statusCode', undefined);
    return (response && statusCode && statusCode === 'INPROGRESS' &&
      response.paymentInfo.paymentStatus === PAYMENT_STATUS_FAILED);
  },
  isProductAvailabilityError(error) {
    return hasValidation(error, 'REGAPI.PRODUCT_NOT_OPEN_FOR_REGISTRATION') ||
      hasValidation(error, 'REGAPI.CAPACITY_UNAVAILABLE');
  },
  isGuestProductAvailabilityError(error) {
    return hasValidation(error, 'REGAPI.PRODUCT_NOT_OPEN_FOR_REGISTRATION') ||
    hasValidation(error, 'REGAPI.CAPACITY_UNAVAILABLE') ||
    hasValidation(error, 'REGAPI.PRODUCT_NOT_AVAILABLE');
  },
  isVoucherAvailabilityError(error) {
    return hasValidation(error, 'REGAPI.REGAPI_VOUCHER_CAPACITY_UNAVAILABLE');
  },
  isAdmissionItemsNotAvailableForRegTypeError(error) {
    return hasValidation(error, 'REGAPI.REGAPI_ADMISSION_CAPACITY_NOT_AVAILABLE_FOR_REGTYPE');
  },
  isAddGroupMemberNotAvailableError(error) {
    return hasValidation(error, 'REGAPI.PRODUCT_NOT_OPEN_FOR_REGISTRATION') ||
      hasValidation(error, 'REGAPI.REGAPI_ADMISSION_CAPACITY_NOT_AVAILABLE') ||
      hasValidation(error, 'REGAPI.CAPACITY_UNAVAILABLE') ||
      hasValidation(error, 'REGAPI.EVENT_NOT_OPEN_FOR_REGISTRATION');
  },
  isCustomFieldAnswerInvalidError(error) {
    return hasValidation(error, 'REGAPI.CUSTOM_FIELD_ANSWER_INVALID_DUE_TO_LOGIC');
  },
  getCustomFieldAnswerInvalidErrors(error) {
    return getValidations(error.responseBody, 'REGAPI.CUSTOM_FIELD_ANSWER_INVALID_DUE_TO_LOGIC')
      .filter(validation => {
        return validation.parametersMap.customField;
      });
  },
  isEventClosed(error) {
    return hasValidation(error, 'REGAPI.EVENT_NOT_OPEN_FOR_REGISTRATION') ||
      hasValidation(error, 'REGAPI.EVENT_NOT_OPEN_FOR_REGISTRATION_INVITEE_STATUS');
  },
  isVoucherCodeInvalid(error) {
    return hasValidation(error, 'REGAPI.EVENT_VOUCHER_CODE_INVALID');
  },
  isEventOpenForWaitlist: (error) => {
    return hasValidation(error, 'REGAPI.OPEN_EVENT_WAITLIST_NOT_ALLOWED');
  },
  isSessionOverlapError: (error) => {
    return hasValidation(error, 'REGAPI.SESSIONS_OVERLAP');
  },
  hasRegTypeCapacityError: (error) => {
    return hasValidation(error, 'REGAPI.CAPACITY_UNAVAILABLE', { entityType: 'ContactType' });
  },
  isDiscountCapacityInsufficient: (error) => {
    return hasValidation(error, 'REGAPI.DISCOUNT_CAPACITY_INSUFFICIENT');
  }
};

export const getRestoreErrors = {
  isRegCartIdConflict(error) {
    return error.responseStatus === 409;
  }
};

export const getUpdateResponseValidations = {
  isPrivateEvent(response) {
    return responseHasValidation(response, 'REGAPI.EMAIL_ONLY_INVITEE')
      || responseHasValidation(response, 'REGAPI.ATTENDEE_NOT_IN_ANY_TARGET_LIST')
      || responseHasValidation(response, 'REGAPI.ATTENDEE_NOT_IN_SPECIFIC_TARGET_LIST')
      || responseHasValidation(response, 'REGAPI.EVENT_TO_EVENT_PREREQUISITE_FAILED')
      || responseHasValidation(response, 'REGAPI.ATTENDEE_ASSIGNED_NOT_ALLOWED_REG_TYPE');
  },
  isAttendeeNotAllowedByCustomLogic(response) {
    return responseHasValidation(response, 'REGAPI.ATTENDEE_ASSIGNED_NOT_ALLOWED_REG_TYPE');
  }
};

export const getCheckoutErrors = {
  admissionItemMissing: (error) => hasValidation(error, 'REGAPI.ADMISSION_ITEM_EXACTLY_ONE'),
  isKnownError: (error) => {
    return hasValidation(error, 'REGAPI.REGAPI_VOUCHER_CODE_REQUIRED')
    || hasValidation(error, 'REGAPI.REGAPI.REGMOD_INVITEE_STATUS_INVALID');
  },
  isPrivateEvent,
  isAttendeeNotAllowedByCustomLogic
};

export const getModErrors = {
  isKnownError(error) {
    return hasValidation(error, 'REGAPI.REGMOD_PAST_MODIFICATION_DATE');
  },
  isTransactionInProcessingError
};

export const getCancelErrors = {
  isTransactionInProcessingError
};

export default function reducer(state = {}, action) {
  switch (action.type) {
    case ADD_GROUP_MEMBER_FAILURE:
    case CREATE_REG_CART_FAILURE:
    case UPDATE_REG_CART_FAILURE:
    case FINALIZE_CHECKOUT_FAILURE:
    case CALCULATE_PRICING_FAILURE:
    case START_MODIFICATION_FAILURE:
    case INITIATE_WAITLIST_REGISTRATION_FAILURE:
    case START_WAITLIST_REGISTRATION_CHECKOUT_FAILURE:
      return {
        type: action.type,
        error: action.payload.error
      };
    default:
      return state;
  }
}

export function isExcludedByPrivacySettings(error, state) {
  if (!error.responseBody) {
    return false;
  }
  if (getUpdateErrors.isInviteeNotFound(error)) {
    const regPathId = getDefaultRegistrationPath(state.appData).id;
    const invitationListAccess = regPathId ? getJSONValue(state.appData,
      accessRulesJsonPath(regPathId, 'invitationListAccess')) : null;
    return invitationListAccess && (
      invitationListAccess.type === PRIVATE_ALL_TARGETED_LISTS ||
      invitationListAccess.type === PRIVATE_LIMITED_TARGETED_LISTS);
  }
  return getUpdateErrors.isPrivateEvent(error);
}
